import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  sendbirdSelectors,
  useSendbirdStateContext,
} from "@sendbird/uikit-react";
import constants from "constants";
import { getChannelUrl } from "Messenger/hooks/channelData.helpers";

const TEAM_TAB = {
  internal: "1",
  external: "2",
};

/**
 * Opens a Sendbird conversation from `?chat=` (web push / deep link).
 * Fetches the channel by URL so office/role filters and pagination cannot block it.
 *
 * @param {object} params
 * @param {(channel: object|null) => void} params.setCurrentChannel
 * @param {(tab: string) => void} [params.setActiveTab]
 * @param {boolean} [params.switchTeamTabs] Switch internal/external tabs from channel type
 * @returns {{ pinnedChannelUrl: string }}
 */
const useChatNotificationDeepLink = ({
  setCurrentChannel,
  setActiveTab,
  switchTeamTabs = false,
}) => {
  const location = useLocation();
  const history = useHistory();
  const context = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(context);
  const isSdkInitialized = Boolean(context?.stores?.sdkStore?.initialized);
  const handledKeyRef = useRef("");
  const [pinnedChannelUrl, setPinnedChannelUrl] = useState("");

  const searchParams = new URLSearchParams(location.search);
  const chatParam = searchParams.get("chat");
  const tabParam = searchParams.get("tab");

  useEffect(() => {
    if (!switchTeamTabs || !setActiveTab || !tabParam) return;
    if (tabParam === TEAM_TAB.external || tabParam === "external") {
      setActiveTab(TEAM_TAB.external);
      return;
    }
    if (tabParam === TEAM_TAB.internal || tabParam === "internal") {
      setActiveTab(TEAM_TAB.internal);
    }
  }, [tabParam, switchTeamTabs, setActiveTab]);

  useEffect(() => {
    if (!chatParam) {
      handledKeyRef.current = "";
      return undefined;
    }

    if (!isSdkInitialized || typeof sdk?.groupChannel?.getChannel !== "function") {
      return undefined;
    }

    const handleKey = `${location.pathname}|${chatParam}`;
    if (handledKeyRef.current === handleKey) return undefined;

    let cancelled = false;

    const openChannel = async () => {
      try {
        const channel = await sdk.groupChannel.getChannel(chatParam);
        if (cancelled || !channel) return;

        if (switchTeamTabs && setActiveTab) {
          if (channel.customType === constants.chat.external) {
            setActiveTab(TEAM_TAB.external);
          } else if (channel.customType === constants.chat.internal) {
            setActiveTab(TEAM_TAB.internal);
          }
        }

        const channelUrl = getChannelUrl(channel);
        setPinnedChannelUrl(channelUrl);
        setCurrentChannel(channel);
        handledKeyRef.current = handleKey;

        const nextParams = new URLSearchParams(location.search);
        nextParams.delete("chat");
        nextParams.delete("tab");
        history.replace({
          pathname: location.pathname,
          search: nextParams.toString(),
        });
      } catch (error) {
        console.error("Failed to open chat from notification:", error);
      }
    };

    openChannel();

    return () => {
      cancelled = true;
    };
  }, [
    chatParam,
    sdk,
    isSdkInitialized,
    setCurrentChannel,
    setActiveTab,
    switchTeamTabs,
    location.pathname,
    location.search,
    history,
  ]);

  return { pinnedChannelUrl };
};

export default useChatNotificationDeepLink;
